import React, { useState, useEffect, useRef } from "react";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { fbStorage } from "./firebase";
import {
  border, navy, red, surface, muted, danger, card, cream, tan, dark,
  NATIONALITY_CODES, CAR_MAKES, YEAR_OPTIONS
} from "./constants";
import { Btn, Input, Textarea, Label, Avatar, Thumb, PortraitCropper, inp } from "./ui";
import { FlagImg } from "./board";

function uid() { return Math.random().toString(36).slice(2, 10); }

export function resizeToBlob(file, maxPx = 600, quality = 0.88) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onerror = rej;
    r.onload = () => {
      const img = new Image();
      img.onerror = rej;
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        c.toBlob(b => b ? res(b) : rej(new Error("toBlob failed")), "image/webp", quality);
      };
      img.src = URL.createObjectURL(new Blob([r.result], { type: file.type || "image/jpeg" }));
    };
    r.readAsArrayBuffer(file);
  });
}

function resizeImage(file, maxPx = 400, quality = 0.85) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onerror = rej;
    r.onload = () => {
      const img = new Image();
      img.onerror = rej;
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        const webp = c.toDataURL("image/webp", quality);
        const jpeg = c.toDataURL("image/jpeg", quality);
        res(webp.length < jpeg.length ? webp : jpeg);
      };
      img.src = URL.createObjectURL(new Blob([r.result], { type: file.type || "image/jpeg" }));
    };
    r.readAsArrayBuffer(file);
  });
}

function toBase64(file) { return resizeImage(file, 400, 0.85); }

export async function uploadToStorage(file, storagePath) {
  const storageRef = ref(fbStorage, storagePath);
  const { uploadBytes, getDownloadURL: getDL } = await import("firebase/storage");
  const snap = await uploadBytes(storageRef, file);
  return await getDownloadURL(snap.ref);
}

function uploadToStorageWithProgress(file, storagePath, onProgress) {
  return new Promise((resolve, reject) => {
    const storageRef = ref(fbStorage, storagePath);
    const task = uploadBytesResumable(storageRef, file);
    task.on("state_changed",
      snap => { if (onProgress) onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)); },
      reject,
      async () => { resolve(await getDownloadURL(task.snapshot.ref)); }
    );
  });
}

export async function deleteFromStorage(url) {
  if (!url || url.startsWith("data:")) return;
  try { await deleteObject(ref(fbStorage, url)); } catch (_) {}
}


export function FuelGauge({ name, animPct, done }) {
  const W = 90, H = 76, cx = W / 2, cy = H - 20, r = 32;
  const toXY = (deg, radius) => {
    const rad = deg * Math.PI / 180;
    return [cx + radius * Math.cos(rad), cy - radius * Math.sin(rad)];
  };
  const needleDeg = 180 - animPct * 180 / 100;
  const tickDegs = [180, 157.5, 135, 112.5, 90, 67.5, 45, 22.5, 0];
  const [eTx] = toXY(180, r - 14);
  const [fTx] = toXY(0, r - 14);
  const needleLen = r - 6;
  const [tipX, tipY] = toXY(needleDeg, needleLen);
  const perpRad = (needleDeg + 90) * Math.PI / 180;
  const baseW = 5.5;
  const b1x = cx + baseW * Math.cos(perpRad), b1y = cy - baseW * Math.sin(perpRad);
  const b2x = cx - baseW * Math.cos(perpRad), b2y = cy + baseW * Math.sin(perpRad);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ flexShrink: 0 }}>
        {tickDegs.map((deg, ti) => {
          const isMajor = ti % 2 === 0;
          const [x1, y1] = toXY(deg, r - 4);
          const [x2, y2] = toXY(deg, r - 4 - (isMajor ? 10 : 7));
          const col = deg >= 135 ? "#cc2222" : "#b0b8c8";
          return <line key={ti} x1={x1.toFixed(2)} y1={y1.toFixed(2)} x2={x2.toFixed(2)} y2={y2.toFixed(2)}
            stroke={col} strokeWidth={isMajor ? 3 : 2} strokeLinecap="round"/>;
        })}
        <path d={`M ${b1x.toFixed(2)} ${b1y.toFixed(2)} L ${tipX.toFixed(2)} ${tipY.toFixed(2)} L ${b2x.toFixed(2)} ${b2y.toFixed(2)} Z`} fill="#cc2222"/>
        <circle cx={cx} cy={cy} r={5} fill="#333"/>
        <circle cx={cx} cy={cy} r={2.5} fill="#111"/>
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", width: W, marginTop: -2, paddingBottom: 2 }}>
        <span style={{ fontSize: 10, fontFamily: "'Bebas Neue',sans-serif", color: "#cc2222",
          marginLeft: Math.round(eTx) - 6 }}>E</span>
        <span style={{ fontSize: 10, fontFamily: "'Bebas Neue',sans-serif", color: "#b0b8c8",
          marginRight: W - Math.round(fTx) - 6 }}>F</span>
      </div>
      <div style={{ textAlign: "center", marginTop: 2 }}>
        {name && <div style={{ fontSize: 8, color: "#6b7a96", fontFamily: "'DM Mono', monospace",
          letterSpacing: "0.06em", overflow: "hidden", textOverflow: "ellipsis",
          whiteSpace: "nowrap", maxWidth: 110, marginBottom: 3 }}>{name}</div>}
        <div style={{ fontSize: 13, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.12em",
          color: done ? "#2a7a4b" : "#f5a800" }}>
          {done ? "Full Tank" : "Refueling..."}
        </div>
      </div>
    </div>
  );
}

export const EMPTY_MEMBER = () => ({ id: uid(), firstName: "", lastName: "", nationality: "", bio: "", photo: null, cropData: null, cars: [], pin: "" });
export const EMPTY_CAR    = () => ({ id: uid(), make: "", model: "", year: "", color: "", note: "", photos: [] });

export function migrateMember(m) {
  if (!m) return m;
  if (m.firstName === undefined) return { ...m, firstName: m.name || "", lastName: m.nickname || "" };
  return m;
}

export function CarEditor({ car, onChange, onDelete, memberId }) {
  const ref = useRef();
  const [uploadProgress, setUploadProgress] = useState([]); // [{name, animPct, done}]
  const uploadTargetsRef = useRef([]); // real progress targets per file
  const animFrameRef = useRef(null);

  function startProgressAnimation(count) {
    uploadTargetsRef.current = new Array(count).fill(0);
    function tick() {
      setUploadProgress(prev => {
        if (!prev.length) return prev;
        let changed = false;
        const next = prev.map((p, i) => {
          const target = uploadTargetsRef.current[i] ?? 0;
          if (p.done) return p;
          const diff = target - p.animPct;
          if (diff < 0.1) return p;
          changed = true;
          // Smooth lerp: moves 3% of remaining distance per frame — no speed jump at any point
          const newPct = p.animPct + diff * 0.03;
          const done = newPct >= 99.9;
          return { ...p, animPct: done ? 100 : newPct, done };
        });
        return changed ? next : prev;
      });
      animFrameRef.current = requestAnimationFrame(tick);
    }
    animFrameRef.current = requestAnimationFrame(tick);
  }

  function stopProgressAnimation() {
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
  }

  async function addPhotos(e) {
    const files = Array.from(e.target.files);
    const remaining = 3 - car.photos.length;
    if (remaining <= 0) { e.target.value = ""; return; }
    const toLoad = files.slice(0, remaining);
    setUploadProgress(toLoad.map(f => ({ name: f.name, animPct: 0, done: false })));
    startProgressAnimation(toLoad.length);
    try {
      const loaded = [];
      for (let i = 0; i < toLoad.length; i++) {
        const f = toLoad[i];
        // Cap real target at 90 until actually done, so needle keeps moving
        uploadTargetsRef.current[i] = 0;
        const blob = await resizeToBlob(f, 1200, 0.88);
        const photoId = uid();
        const storagePath = `members/${memberId}/cars/${car.id}/${photoId}.webp`;
        const url = await uploadToStorageWithProgress(blob, storagePath, pct => {
          uploadTargetsRef.current[i] = pct;
        });
        // Now actually done — push to 100
        uploadTargetsRef.current[i] = 100;
        // Wait for animation to catch up to 100
        await new Promise(res => {
          const check = setInterval(() => {
            setUploadProgress(prev => {
              if (prev[i]?.done) { clearInterval(check); res(); }
              return prev;
            });
          }, 50);
        });
        loaded.push({ id: photoId, data: url, storagePath });
      }
      onChange({ ...car, photos: [...car.photos, ...loaded] });
    } catch (err) {
      alert("Photo upload failed: " + err.message);
    }
    stopProgressAnimation();
    setUploadProgress([]);
    e.target.value = "";
  }
  return (
    <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: 3, padding: "12px 12px 10px", marginTop: 10 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
        <div>
          <Label>Year</Label>
          <select value={car.year} onChange={e => onChange({ ...car, year: e.target.value })}
            style={{ ...inp, background: "#fff", appearance: "none", WebkitAppearance: "none" }}>
            <option value="">— Select Year —</option>
            {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <Label>Make</Label>
          <select value={car.make} onChange={e => onChange({ ...car, make: e.target.value })}
            style={{ ...inp, background: "#fff", appearance: "none", WebkitAppearance: "none" }}>
            <option value="">— Select Make —</option>
            {CAR_MAKES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div><Label>Model</Label><Input value={car.model} placeholder="e.g. 911 Carrera" onChange={e => onChange({ ...car, model: e.target.value })} /></div>
      </div>
      <Textarea value={car.note} placeholder="Notes (spec, history, mods…)"
        onChange={e => onChange({ ...car, note: e.target.value })} style={{ marginBottom: 10 }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", flexDirection: "row", gap: 8, alignItems: "flex-start" }}>
          {car.photos.map((p, idx) => <Thumb key={p.id} src={p.data} size={90}
            isFeature={idx === 0}
            onFeature={idx !== 0 ? () => {
              const newPhotos = [p, ...car.photos.filter(x => x.id !== p.id)];
              onChange({ ...car, photos: newPhotos });
            } : null}
            onRemove={() => onChange({ ...car, photos: car.photos.filter(x => x.id !== p.id) })} />)}
          {uploadProgress.length > 0 && (
            <div style={{ flex: 1, display: "flex", flexDirection: "row", gap: 0 }}>
              {uploadProgress.map((p, i) => {
                const W = 90, H = 76, cx = W / 2, cy = H - 20, r = 32;
                const toXY = (deg, radius) => {
                  const rad = deg * Math.PI / 180;
                  return [cx + radius * Math.cos(rad), cy - radius * Math.sin(rad)];
                };
                const needleDeg = 180 - p.animPct * 180 / 100;
                const tickDegs = [180, 157.5, 135, 112.5, 90, 67.5, 45, 22.5, 0];
                const [eTx] = toXY(180, r - 14);
                const [fTx] = toXY(0, r - 14);
                const needleLen = r - 6;
                const [tipX, tipY] = toXY(needleDeg, needleLen);
                const perpRad = (needleDeg + 90) * Math.PI / 180;
                const baseW = 5.5;
                const b1x = cx + baseW * Math.cos(perpRad), b1y = cy - baseW * Math.sin(perpRad);
                const b2x = cx - baseW * Math.cos(perpRad), b2y = cy + baseW * Math.sin(perpRad);
                return (
                  <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1,
                    ...(i > 0 ? { borderLeft: `1px solid ${border}` } : {}) }}>
                    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ flexShrink: 0 }}>
                      {tickDegs.map((deg, ti) => {
                        const isMajor = ti % 2 === 0;
                        const [x1, y1] = toXY(deg, r - 4);
                        const [x2, y2] = toXY(deg, r - 4 - (isMajor ? 10 : 7));
                        const col = deg >= 135 ? "#cc2222" : "#b0b8c8";
                        return <line key={ti} x1={x1.toFixed(2)} y1={y1.toFixed(2)} x2={x2.toFixed(2)} y2={y2.toFixed(2)}
                          stroke={col} strokeWidth={isMajor ? 3 : 2} strokeLinecap="round"/>;
                      })}
                      <path d={`M ${b1x.toFixed(2)} ${b1y.toFixed(2)} L ${tipX.toFixed(2)} ${tipY.toFixed(2)} L ${b2x.toFixed(2)} ${b2y.toFixed(2)} Z`} fill="#cc2222"/>
                      <circle cx={cx} cy={cy} r={5} fill="#333"/>
                      <circle cx={cx} cy={cy} r={2.5} fill="#111"/>
                    </svg>
                    <div style={{ display: "flex", justifyContent: "space-between", width: W, marginTop: -2, paddingBottom: 2 }}>
                      <span style={{ fontSize: 10, fontFamily: "'Bebas Neue',sans-serif", color: "#cc2222",
                        marginLeft: Math.round(eTx) - 6 }}>E</span>
                      <span style={{ fontSize: 10, fontFamily: "'Bebas Neue',sans-serif", color: "#b0b8c8",
                        marginRight: W - Math.round(fTx) - 6 }}>F</span>
                    </div>
                    <div style={{ textAlign: "center", marginTop: 2 }}>
                      <div style={{ fontSize: 8, color: muted, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 110, marginBottom: 3 }}>
                        {p.name}
                      </div>
                      <div style={{ fontSize: 13, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.12em",
                        color: p.done ? "#2a7a4b" : "#f5a800" }}>
                        {p.done ? "Full Tank" : "Refueling..."}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {uploadProgress.length === 0 && car.photos.length < 3 && (
            <Btn variant="ghost" style={{ padding: "13px 22px", fontSize: 13 }} onClick={() => ref.current.click()}>
              + Photo ({car.photos.length}/3)
            </Btn>
          )}
        </div>
        {uploadProgress.length === 0 && car.photos.length >= 3 && (
          <span style={{ fontSize: 11, color: muted, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em" }}>
            Max 3 photos
          </span>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*,image/heic,image/heif" multiple style={{ display: "none" }} onChange={addPhotos} />
      <div style={{ marginTop: 10, textAlign: "right" }}>
        <Btn variant="danger" style={{ padding: "12px 18px", fontSize: 13 }} onClick={onDelete}>Remove</Btn>
      </div>
    </div>
  );
}

export function MemberModal({ member: init, onSave, onCancel, onDelete, isAdmin }) {
  const [m, setM] = useState(init ? JSON.parse(JSON.stringify(init)) : EMPTY_MEMBER());
  const [showCropper, setShowCropper] = useState(false);
  const [rawPhoto, setRawPhoto] = useState(null);
  const [errors, setErrors] = useState({});
  const [confirmResetPin, setConfirmResetPin] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pinWasReset, setPinWasReset] = useState(false);
  const pRef = useRef();
  const nameRef = useRef();
  const lastNameRef = useRef();
  const pinRef = useRef();
  const modalRef = useRef();
  const setF = k => e => setM(x => ({ ...x, [k]: e.target.value }));
  const isNew = !init;

  function validate() {
    const e = {};
    if (!m.firstName.trim()) e.name = true;
    if (!isAdmin && (pinWasReset || isNew) && m.pin.trim().length !== 4) e.pin = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSave() {
    const e = {};
    if (!m.firstName.trim()) e.name = true;

    if (!isAdmin && (pinWasReset || isNew) && m.pin.trim().length !== 4) e.pin = true;
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const firstRef = e.firstName ? nameRef : e.lastName ? lastNameRef : e.pin ? pinRef : null;
      if (firstRef?.current && modalRef?.current) {
        const container = modalRef.current;
        const el = firstRef.current;
        container.scrollTo({ top: el.offsetTop - 20, behavior: "smooth" });
      }
      return;
    }
    onSave(m);
  }

  async function loadPortrait(e) {
    const f = e.target.files[0]; if (!f) return;
    const data = await toBase64(f);
    setRawPhoto(data);
    setM(x => ({ ...x, photo: data, cropData: null }));
    setShowCropper(true);
  }

  return (
    <div ref={modalRef} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 200,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      overflowY: "auto", padding: "20px 12px", WebkitOverflowScrolling: "touch" }}>
      {showCropper && (rawPhoto || m.photo) && (
        <PortraitCropper
          src={rawPhoto || m.photo}
          cropData={m.cropData}
          onChange={cd => setM(x => ({ ...x, cropData: cd }))}
          onClose={() => setShowCropper(false)}
        />
      )}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 6,
        width: "100%", maxWidth: 600, padding: "24px 20px",
        fontFamily: "'DM Mono', monospace", animation: "fadeUp 0.3s ease" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22,
          borderBottom: `1px solid ${border}`, paddingBottom: 16 }}>
          <div style={{ width: 3, height: 20, background: red, borderRadius: 2 }} />
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20,
            color: cream, letterSpacing: "0.2em" }}>
            {init ? "Edit Member" : "Add Member"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 18, alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <div onClick={() => pRef.current.click()} style={{
              width: 80, height: 80, borderRadius: "50%",
              background: surface, border: `2px solid ${border}`,
              cursor: "pointer", position: "relative", flexShrink: 0,
              overflow: "hidden",
            }}>
              {m.photo ? (
                m.cropData
                  ? (() => {
                      const CROP_SIZE = 220;
                      const avatarSize = 80;
                      const scale = avatarSize / CROP_SIZE;
                      const cd = m.cropData;
                      return <img src={m.photo} alt="" loading="lazy" style={{
                        position: "absolute",
                        width: cd.natW ? cd.natW * cd.zoom * scale : avatarSize * cd.zoom,
                        height: cd.natH ? cd.natH * cd.zoom * scale : avatarSize * cd.zoom,
                        top: "50%", left: "50%",
                        transform: `translate(calc(-50% + ${cd.ox * scale}px), calc(-50% + ${cd.oy * scale}px))`,
                        pointerEvents: "none", maxWidth: "none", display: "block",
                      }} />;
                    })()
                  : <img src={m.photo} alt="" loading="lazy" style={{
                      position: "absolute", top: 0, left: 0,
                      width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none",
                    }} />
              ) : (
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 28, color: muted }}>+</span>
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <Btn variant="ghost" style={{ padding: "12px 18px", fontSize: 13 }}
                onClick={() => pRef.current.click()}>Change</Btn>
              {m.photo && (
                <Btn variant="navy" style={{ padding: "12px 18px", fontSize: 13 }}
                  onClick={() => setShowCropper(true)}>Crop</Btn>
              )}
            </div>
            <input ref={pRef} type="file" accept="image/*" style={{ display: "none" }} onChange={loadPortrait} />
          </div>
          <div style={{ flex: 1, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <Label>First Name <span style={{color:red}}>*</span></Label>
              <Input ref={nameRef} value={m.firstName} placeholder="James" onChange={setF("firstName")}
                style={{ borderColor: errors.firstName ? danger : border }} />
              {errors.firstName && <div style={{fontSize:11,color:danger,marginTop:3}}>Required</div>}
            </div>
            <div>
              <Label>Last Name</Label>
              <Input ref={lastNameRef} value={m.lastName} placeholder="Smith" onChange={setF("lastName")}
                style={{ borderColor: errors.lastName ? danger : border }} />
              {errors.lastName && <div style={{fontSize:11,color:danger,marginTop:3}}>Required</div>}
            </div>
            <div>
              <Label>Nationality</Label>
              <select value={m.nationality} onChange={setF("nationality")}
                style={{ ...inp, width: "100%", appearance: "none", backgroundImage: "none",
                  paddingRight: 28, cursor: "pointer", color: m.nationality ? navy : muted }}>
                <option value="">— Select —</option>
                {["American", "Argentinian", "Australian", "Austrian", "Belgian", "Brazilian", "British", "Canadian", "Chilean", "Chinese", "Colombian", "Danish", "Dutch", "Emirati", "Filipino", "Finnish", "French", "German", "Greek", "Hong Konger", "Hungarian", "Indian", "Indonesian", "Irish", "Israeli", "Italian", "Japanese", "Korean", "Lebanese", "Malaysian", "Mexican", "New Zealander", "Norwegian", "Pakistani", "Polish", "Portuguese", "Romanian", "Russian", "Saudi", "Singaporean", "South African", "Spanish", "Swedish", "Swiss", "Taiwanese", "Thai", "Turkish", "Vietnamese", "Other"].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            {(isNew || pinWasReset) && !isAdmin && (
              <div>
                <Label>{isNew ? "4-Digit PIN" : "Set New PIN"} <span style={{color:red}}>*</span></Label>
                <Input ref={pinRef} value={m.pin} placeholder="1234" maxLength={4}
                  onChange={e => setM(x => ({ ...x, pin: e.target.value.replace(/\D/g,"").slice(0,4) }))}
                  style={{ borderColor: errors.pin ? danger : border, letterSpacing:"0.3em", fontSize:18 }} />
                {errors.pin && <div style={{fontSize:11,color:danger,marginTop:3}}>Please set a 4-digit PIN</div>}
                <div style={{fontSize:11,color:muted,marginTop:4}}>{isNew ? "Remember your PIN — you will need it to edit your profile" : "Your PIN was reset. Please set a new one."}</div>
              </div>
            )}
            {!isNew && isAdmin && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 12px", background: `${navy}10`, borderRadius: 4, border: `1px solid ${border}`, overflow: "hidden" }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
                    color: navy, fontFamily: "'DM Mono', monospace", marginBottom: 2 }}>Member PIN</div>
                  <div style={{ fontSize: 11, color: muted }}>
                    {m.pin ? `Set (${m.pin.length} digits)` : "Not set"}
                  </div>
                </div>
                {confirmResetPin ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                    <span style={{ fontSize: 10, color: muted, fontFamily: "'DM Mono', monospace" }}>Reset PIN?</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Btn variant="ghost" style={{ padding: "5px 10px", fontSize: 11 }}
                        onClick={() => setConfirmResetPin(false)}>
                        Cancel
                      </Btn>
                      <Btn variant="danger" style={{ padding: "5px 10px", fontSize: 11 }}
                        onClick={() => { setM(x => ({ ...x, pin: "" })); setConfirmResetPin(false); setPinWasReset(true); }}>
                        Reset
                      </Btn>
                    </div>
                  </div>
                ) : (
                  <Btn variant="danger" style={{ padding: "8px 14px", fontSize: 11 }}
                    onClick={() => setConfirmResetPin(true)}>
                    Reset PIN
                  </Btn>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <Label>Bio (optional)</Label>
          <Textarea value={m.bio} placeholder="Interests, favorite roads…" onChange={setF("bio")} />
        </div>
        <div style={{ borderTop: `1px solid ${border}`, paddingTop: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Label style={{ marginBottom: 0 }}>Vehicles</Label>
            <Btn variant="ghost" style={{ padding: "13px 22px", fontSize: 13 }}
              onClick={() => setM(x => ({ ...x, cars: [...x.cars, EMPTY_CAR()] }))}>+ Add Vehicle</Btn>
          </div>
          {m.cars.length === 0 && <div style={{ color: muted, fontSize: 12 }}>No vehicles registered</div>}
          {m.cars.map((car, i) => (
            <CarEditor key={car.id} car={car} memberId={m.id}
              onChange={c => setM(x => { const cars = [...x.cars]; cars[i] = c; return { ...x, cars }; })}
              onDelete={() => setM(x => ({ ...x, cars: x.cars.filter((_, j) => j !== i) }))} />
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {init && onDelete && (
            confirmDelete ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginRight: "auto" }}>
                <span style={{ fontSize: 11, color: danger, fontFamily: "'DM Mono', monospace" }}>Delete member?</span>
                <Btn variant="ghost" style={{ padding: "6px 12px", fontSize: 11 }} onClick={() => setConfirmDelete(false)}>No</Btn>
                <Btn variant="danger" style={{ padding: "6px 12px", fontSize: 11 }} onClick={() => onDelete()}>Yes, Delete</Btn>
              </div>
            ) : (
              <Btn variant="danger" style={{ marginRight: "auto" }} onClick={() => setConfirmDelete(true)}>Delete Member</Btn>
            )
          )}
          <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
          <Btn onClick={handleSave}>Save</Btn>
        </div>
      </div>
    </div>
  );
}


function MemberBadge({ member }) {
  const now = Date.now();
  const NEW_MS  = 14 * 24 * 60 * 60 * 1000; // 14 days
  const UPD_MS  =  7 * 24 * 60 * 60 * 1000; //  7 days
  // createdAt がない既存メンバーは updatedAt を代替として使用
  const createdAt = member.createdAt || member.updatedAt;
  const isNew     = createdAt && (now - createdAt) < NEW_MS;
  const isUpdated = !isNew && member.updatedAt && createdAt &&
                    member.updatedAt > createdAt &&
                    (now - member.updatedAt) < UPD_MS;
  if (!isNew && !isUpdated) return null;
  return (
    <div style={{
      position: "absolute", top: 8, right: 8,
      background: isNew ? "#C0342B" : "#8A6A3A",
      color: "#fff", fontSize: 8, fontWeight: 700,
      letterSpacing: "0.12em", padding: "2px 6px",
      borderRadius: 3, fontFamily: "'DM Mono', monospace",
      textTransform: "uppercase", lineHeight: 1.4,
      pointerEvents: "none",
    }}>
      {isNew ? "New" : "Update"}
    </div>
  );
}

export function FaceCard({ member, onDetail, idx, memberNum }) {
  const [hov, setHov] = useState(false);
  return (
    <div className="card-item fade-up" onClick={() => onDetail(member)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: card, border: `1px solid ${hov ? `${navy}40` : border}`, borderRadius: 6,
        display: "flex", flexDirection: "column", alignItems: "center",
        padding: "16px 12px 12px", cursor: "pointer", animationDelay: `${idx * 0.03}s`,
        WebkitTapHighlightColor: "transparent", position: "relative",
        boxShadow: hov ? `0 4px 16px rgba(26,37,64,0.12)` : `0 1px 4px rgba(26,37,64,0.05)`,
        transition: "box-shadow 0.15s, border-color 0.15s" }}>
      <MemberBadge member={member} />
      <Avatar photo={member.photo} cropData={member.cropData} size={72} />
      <div style={{ marginTop: 8, textAlign: "center", width: "100%" }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20,
          color: navy, letterSpacing: "0.1em", lineHeight: 1.1,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {member.firstName || "(No name)"}
        </div>
        {member.lastName && (
          <div style={{ fontSize: 11, color: tan, fontFamily: "'DM Mono', monospace",
            letterSpacing: "0.06em", marginTop: 2,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {member.lastName}
          </div>
        )}
        {member.cars.length > 0 && (
          <div style={{ fontSize: 11, color: red, letterSpacing: "0.15em", marginTop: 4,
            fontFamily: "'DM Mono', monospace" }}>
            {member.cars.length} {member.cars.length === 1 ? "vehicle" : "vehicles"}
          </div>
        )}
      </div>
    </div>
  );
}

export function CarCard({ member, carIdx = 0, onDetail, idx, memberNum }) {
  const car = member.cars[carIdx] ?? null;
  const [hov, setHov] = useState(false);
  return (
    <div className="card-item fade-up" onClick={() => onDetail(member)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: card, border: `1px solid ${hov ? `${navy}40` : border}`, borderRadius: 6,
        overflow: "hidden", cursor: "pointer", position: "relative",
        animationDelay: `${idx * 0.03}s`,
        WebkitTapHighlightColor: "transparent",
        boxShadow: hov ? `0 4px 16px rgba(26,37,64,0.12)` : `0 1px 4px rgba(26,37,64,0.05)`,
        transition: "box-shadow 0.15s, border-color 0.15s" }}>
      <MemberBadge member={member} />
      <div style={{ width: "100%", paddingBottom: "62%", position: "relative", background: surface }}>
        {car?.photos?.[0]
          ? <img src={car.photos[0].data} alt="" loading="lazy"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          : <div style={{ position: "absolute", inset: 0, display: "flex",
              alignItems: "center", justifyContent: "center", fontSize: 11,
              color: `${navy}30`, letterSpacing: "0.15em", fontFamily: "'DM Mono', monospace" }}>NO PHOTO</div>
        }

      </div>
      <div style={{ padding: "8px 12px 10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, lineHeight: 1.1 }}>
          {car?.make && (() => {
            const slug = car.make.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-");
            return (
              <img
                src={`https://www.carlogos.org/car-logos/${slug}-logo.png`}
                alt={car.make}
                onError={e => { e.currentTarget.style.display = "none"; }}
                style={{ height: 18, width: "auto", maxWidth: 36, objectFit: "contain", flexShrink: 0 }}
              />
            );
          })()}
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18,
            color: navy, letterSpacing: "0.1em",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {car?.model || "—"}
          </div>
        </div>
        <div style={{ fontSize: 10, color: muted, fontFamily: "'DM Mono', monospace",
          marginTop: 3, letterSpacing: "0.08em" }}>
          {car?.year || ""}
        </div>
      </div>
    </div>
  );
}
export function Lightbox({ src, onClose }) {
  useEffect(() => {
    const h = e => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div onClick={e => { e.stopPropagation(); onClose(); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.97)",
      zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out",
      touchAction: "manipulation" }}>
      <img src={src} alt="" onClick={e => { e.stopPropagation(); onClose(); }}
        style={{ maxWidth: "94vw", maxHeight: "90vh", objectFit: "contain" }} />
    </div>
  );
}

export function MemberDetail({ member, isAdmin, onEdit, onClose }) {
  const [open, setOpen] = useState(null);
  const [lb, setLb] = useState(null);
  useEffect(() => {
    const h = e => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px 12px", WebkitTapHighlightColor: "transparent" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: card, borderRadius: 10, width: "100%",
          maxWidth: 520, maxHeight: "88vh", overflowY: "auto", position: "relative",
          boxShadow: "0 8px 40px rgba(0,0,0,0.4)", animation: "fadeUp 0.25s ease" }}>
        <div style={{ width: 40, height: 4, background: `${navy}30`, borderRadius: 2,
          margin: "10px auto 0" }} />
        <button onClick={onClose}
          style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none",
            cursor: "pointer", fontSize: 22, color: muted, lineHeight: 1, padding: "4px 8px",
            borderRadius: 6, zIndex: 10 }}>×</button>
        <div style={{ padding: "14px 16px 10px", display: "flex", gap: 14, alignItems: "center" }}>
          <div onClick={() => member.photo && setLb(member.photo)}
            style={{ cursor: member.photo ? "zoom-in" : "default", flexShrink: 0 }}>
            <Avatar photo={member.photo} cropData={member.cropData} size={64} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {member.lastName ? (
              <>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30,
                  color: navy, letterSpacing: "0.1em", lineHeight: 1 }}>{member.firstName}</div>
                <div style={{ fontSize: 13, color: tan, fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: "0.06em" }}>{member.firstName} {member.lastName}</div>
              </>
            ) : (
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30,
                color: navy, letterSpacing: "0.1em", lineHeight: 1 }}>{member.firstName || "(No name)"}</div>
            )}
            {member.bio && (
              <div style={{ fontSize: 14, color: muted, marginTop: 4, lineHeight: 1.7,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>{member.bio}</div>
            )}
          </div>
          {/* Right col: Edit + flag */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, flexShrink: 0 }}>
            {member.nationality && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                <FlagImg nationality={member.nationality} size={22} />
                <div style={{ fontSize: 9, color: muted, fontFamily: "'DM Mono', monospace",
                  letterSpacing: "0.06em", textAlign: "center" }}>{member.nationality}</div>
              </div>
            )}
            <Btn variant="ghost" style={{ padding: "10px 16px", fontSize: 13 }} onClick={onEdit}>Edit</Btn>
          </div>
        </div>
        {member.cars.length > 0 && (
          <div style={{ padding: "4px 16px 8px", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: red, letterSpacing: "0.2em",
              fontFamily: "'DM Mono', monospace", textTransform: "uppercase" }}>
              {member.cars.length} {member.cars.length === 1 ? "vehicle" : "vehicles"}
            </span>
            <div style={{ height: 1, flex: 1, background: `linear-gradient(to right, ${red}25, transparent)` }} />
          </div>
        )}
        {member.cars.map(car => (
          <div key={car.id} style={{ borderTop: `1px solid ${border}` }}>
            <div onClick={() => setOpen(open === car.id ? null : car.id)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
                cursor: "pointer", background: open === car.id ? surface : "transparent",
                transition: "background 0.15s", WebkitTapHighlightColor: "transparent" }}>
              {car.photos[0]
                ? <img src={car.photos[0].data} alt=""
                    onClick={e => { e.stopPropagation(); setLb(car.photos[0].data); }}
                    style={{ width: 50, height: 36, objectFit: "cover", borderRadius: 2,
                      border: `1px solid ${border}`, cursor: "zoom-in", flexShrink: 0 }} />
                : <div style={{ width: 50, height: 36, background: surface, borderRadius: 2,
                    flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 9, color: `${navy}40`, letterSpacing: "0.1em", fontFamily: "'DM Mono', monospace" }}>NO PHOTO</div>
              }
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18,
                  color: navy, letterSpacing: "0.08em" }}>
                  {[car.year, car.make, car.model].filter(Boolean).join(" ") || "(No name)"}
                </div>
                {car.color && <div style={{ fontSize: 12, color: tan,
                  fontFamily: "'DM Mono', monospace" }}>{car.color}</div>}
              </div>
              <div style={{ fontSize: 12, color: red }}>{open === car.id ? "▲" : "▼"}</div>
            </div>
            {open === car.id && (
              <div style={{ padding: "12px 16px 16px", background: surface }}>
                {car.note && <p style={{ fontSize: 14, color: navy, lineHeight: 1.7, marginBottom: 10,
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>{car.note}</p>}
              <div style={{ display: "flex", overflowX: "auto", gap: 8,
                  paddingBottom: 4, WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none" }}>
                  {car.photos.map(p => <Thumb key={p.id} src={p.data} size={100}
                    onClick={() => setLb(p.data)} />)}
                </div>
              </div>
            )}
          </div>
        ))}
        <div style={{ height: 24 }} />
      </div>
      {lb && <Lightbox src={lb} onClose={() => setLb(null)} />}
    </div>
  );
}

export function PinPrompt({ member, onVerified, onSetPin, onClose }) {
  // mode: "verify" = existing PIN, "set" = first-time PIN setup
  const mode = member.pin ? "verify" : "set";
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [shaking, setShaking] = useState(false);

  function shake() { setShaking(true); setTimeout(() => setShaking(false), 450); }

  function attempt() {
    if (mode === "verify") {
      if (pin === member.pin) { onVerified(); }
      else { setErr("Incorrect PIN"); shake(); setPin(""); }
    } else {
      if (pin.length !== 4) { setErr("Please enter a 4-digit PIN"); shake(); return; }
      if (pin !== confirm) { setErr("PINs do not match"); shake(); setConfirm(""); return; }
      onSetPin(pin);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 250,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div className={shaking ? "shake" : ""}
        style={{ background: card, border: `1px solid ${border}`, borderRadius: 6,
          padding: "28px 24px", width: "100%", maxWidth: 300, fontFamily: "'DM Mono', monospace" }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: cream,
          letterSpacing: "0.2em", marginBottom: 4 }}>
          {mode === "verify" ? "EDIT PROFILE" : "SET YOUR PIN"}
        </div>
        <div style={{ fontSize: 12, color: muted, marginBottom: 20 }}>
          {mode === "verify"
            ? "Enter your 4-digit PIN to continue"
            : "First time here. Please set a 4-digit PIN."}
        </div>

        <Label>{mode === "verify" ? "PIN" : "New PIN"}</Label>
        <Input type="password" value={pin} placeholder="••••" maxLength={4}
          onChange={e => { setPin(e.target.value.replace(/\D/g,"").slice(0,4)); setErr(""); }}
          onKeyDown={e => e.key === "Enter" && (mode === "verify" ? attempt() : confirm.length === 4 ? attempt() : null)}
          autoFocus
          style={{ marginBottom: 12, borderColor: err ? danger : border,
            fontSize: 22, letterSpacing: "0.4em", textAlign: "center" }} />

        {mode === "set" && (
          <>
            <Label>Confirm PIN</Label>
            <Input type="password" value={confirm} placeholder="••••" maxLength={4}
              onChange={e => { setConfirm(e.target.value.replace(/\D/g,"").slice(0,4)); setErr(""); }}
              onKeyDown={e => e.key === "Enter" && attempt()}
              style={{ marginBottom: 12, borderColor: err ? danger : border,
                fontSize: 22, letterSpacing: "0.4em", textAlign: "center" }} />
          </>
        )}

        {err && <div style={{ fontSize: 12, color: danger, marginBottom: 14 }}>{err}</div>}
        {!err && <div style={{ marginBottom: 14 }} />}

        <div style={{ display: "flex", gap: 10 }}>
          <Btn variant="ghost" style={{ flex: 1, padding: "12px" }} onClick={onClose}>Cancel</Btn>
          <Btn style={{ flex: 1, padding: "12px" }} onClick={attempt}>
            {mode === "verify" ? "Confirm" : "Set PIN"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── Batch Upload Modal ──────────────────────────────────────────────────────
